import { useState } from 'react';
import type { Block, BlockType, Design, Finding } from '../lib/types.js';
import {
  addBlock,
  allColumns,
  BLOCK_LABELS,
  blockLabel,
  duplicateBlock,
  flatten,
  LEAF_TYPES,
  moveBlock,
  newBlock,
  removeBlock,
  reorderTopLevel,
} from '../lib/design.js';

/**
 * The structure panel: an outline of the design with drag-to-reorder for
 * top-level blocks and explicit move buttons everywhere.
 *
 * Both are present on purpose. Dragging is the natural gesture, but it is
 * unreliable for nested items and inaccessible by keyboard, so the arrow
 * buttons are the primary mechanism and dragging is the shortcut.
 */
export function BlockOutline({
  design,
  selectedId,
  findings,
  readOnly,
  onSelect,
  onChange,
}: {
  design: Design;
  selectedId: string | null;
  findings: Finding[];
  readOnly: boolean;
  onSelect: (id: string | null) => void;
  onChange: (next: Design) => void;
}): JSX.Element {
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropId, setDropId] = useState<string | null>(null);
  const [addTarget, setAddTarget] = useState<string>('');

  const entries = flatten(design);
  const columns = allColumns(design);

  // Highest severity per block, so the outline shows where the problems are.
  const worstByBlock = new Map<string, Finding['severity']>();
  for (const finding of findings) {
    if (!finding.blockId) continue;
    const current = worstByBlock.get(finding.blockId);
    if (!current || rank(finding.severity) < rank(current)) {
      worstByBlock.set(finding.blockId, finding.severity);
    }
  }

  const add = (type: BlockType) => {
    const block = newBlock(type);
    onChange(addBlock(design, block, type === 'row' ? undefined : addTarget || undefined));
    onSelect(block.id);
  };

  return (
    <div className="card">
      <div className="card-head">
        <h2>Structure</h2>
        <span className="tiny muted">{entries.length} blocks</span>
      </div>

      {entries.length === 0 ? (
        <p className="muted" style={{ padding: '14px' }}>
          Empty template. Add a block below.
        </p>
      ) : (
        <ul className="block-list">
          {entries.map((entry) => {
            const worst = worstByBlock.get(entry.block.id);
            const topLevel = entry.depth === 0;
            return (
              <li
                key={entry.block.id}
                className={[
                  'block-item',
                  entry.block.id === selectedId ? 'selected' : '',
                  entry.depth > 0 ? 'nested' : '',
                  dragId === entry.block.id ? 'dragging' : '',
                  dropId === entry.block.id ? 'drop-target' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                draggable={!readOnly && topLevel}
                onDragStart={() => setDragId(entry.block.id)}
                onDragEnd={() => {
                  setDragId(null);
                  setDropId(null);
                }}
                onDragOver={(e) => {
                  if (!dragId || !topLevel || dragId === entry.block.id) return;
                  e.preventDefault();
                  setDropId(entry.block.id);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (dragId && topLevel && dragId !== entry.block.id) {
                    onChange(reorderTopLevel(design, dragId, entry.block.id));
                  }
                  setDragId(null);
                  setDropId(null);
                }}
                onClick={() => onSelect(entry.block.id)}
              >
                {!readOnly && topLevel && (
                  <span className="handle" aria-hidden="true" title="Drag to reorder">
                    ⠿
                  </span>
                )}
                <span className="kind">{BLOCK_LABELS[entry.block.type]}</span>
                <span className="label">{blockLabel(entry.block)}</span>
                {worst && (
                  <span
                    className="flag"
                    style={{ color: worst === 'error' ? 'var(--danger)' : 'var(--warn)' }}
                    title={`Has ${worst}-level findings`}
                  >
                    {worst === 'error' ? '●' : '▲'}
                  </span>
                )}
                {!readOnly && (
                  <span className="row" style={{ gap: 2 }}>
                    <button
                      className="ghost icon"
                      title="Move up"
                      aria-label={`Move ${blockLabel(entry.block)} up`}
                      disabled={entry.index === 0}
                      onClick={(e) => {
                        e.stopPropagation();
                        onChange(moveBlock(design, entry.block.id, -1));
                      }}
                    >
                      ↑
                    </button>
                    <button
                      className="ghost icon"
                      title="Move down"
                      aria-label={`Move ${blockLabel(entry.block)} down`}
                      disabled={entry.index === entry.siblingCount - 1}
                      onClick={(e) => {
                        e.stopPropagation();
                        onChange(moveBlock(design, entry.block.id, 1));
                      }}
                    >
                      ↓
                    </button>
                    <button
                      className="ghost icon"
                      title="Duplicate"
                      aria-label={`Duplicate ${blockLabel(entry.block)}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onChange(duplicateBlock(design, entry.block.id));
                      }}
                    >
                      ⧉
                    </button>
                    <button
                      className="ghost icon"
                      title="Delete"
                      aria-label={`Delete ${blockLabel(entry.block)}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onChange(removeBlock(design, entry.block.id));
                        if (selectedId === entry.block.id) onSelect(null);
                      }}
                    >
                      ×
                    </button>
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {!readOnly && (
        <>
          {columns.length > 0 && (
            <div style={{ padding: '8px 8px 0' }}>
              <label className="field-label" htmlFor="add-target">
                Add into
              </label>
              <select
                id="add-target"
                value={addTarget}
                onChange={(e) => setAddTarget(e.target.value)}
              >
                <option value="">Top level</option>
                {columns.map((c) => (
                  <option key={c.column.id} value={c.column.id}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="palette">
            {LEAF_TYPES.map((type) => (
              <button key={type} type="button" onClick={() => add(type)}>
                + {BLOCK_LABELS[type]}
              </button>
            ))}
            <button
              type="button"
              style={{ gridColumn: '1 / -1' }}
              onClick={() => add('row')}
              title="A row of side-by-side columns, e.g. logo on the left and details on the right"
            >
              + Columns row
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function rank(severity: Finding['severity']): number {
  return severity === 'error' ? 0 : severity === 'warning' ? 1 : 2;
}

/** Type guard used by the inspector to narrow the selected block. */
export function isType<T extends BlockType>(
  block: Block | null | undefined,
  type: T,
): block is Extract<Block, { type: T }> {
  return Boolean(block && block.type === type);
}
